import React, { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';
import { formatNumber } from '../../../components/design-system/admin';

export interface CompareRow {
    key: string;
    label: ReactNode;
    you: number | null | undefined;
    them: number | null | undefined;
    format?: (v: number) => string;
}

/**
 * They against you, one measure per row: two short bars on the same scale,
 * yours in ink and theirs lighter, so who is ahead reads before the numbers.
 */
export const CompareBars: React.FC<{ rows: CompareRow[]; rival: string; className?: string }> = ({ rows, rival, className }) => {
    const { t } = useTranslation();
    const shown = rows.filter((r) => r.you != null || r.them != null);
    if (!shown.length) return null;
    return (
        <div className={cn('flex flex-col gap-3.5', className)}>
            <div className="flex items-center gap-4 text-xs text-muted-foreground">
                <span className="inline-flex items-center gap-1.5">
                    <span className="size-2 rounded-[3px] bg-foreground" aria-hidden />
                    {t('BUSINESS.COMMON.YOU')}
                </span>
                <span className="inline-flex min-w-0 items-center gap-1.5">
                    <span className="size-2 shrink-0 rounded-[3px] bg-foreground/30" aria-hidden />
                    <span className="truncate">{rival}</span>
                </span>
            </div>
            {shown.map((r) => {
                const you = r.you ?? 0;
                const them = r.them ?? 0;
                const top = Math.max(you, them, 1);
                const f = r.format ?? ((v: number) => formatNumber(v));
                return (
                    <div key={r.key} className="grid grid-cols-[minmax(0,7rem)_1fr] items-center gap-x-3 gap-y-1">
                        <span className="truncate text-xs text-muted-foreground">{r.label}</span>
                        <div className="flex min-w-0 flex-col gap-1">
                            <Bar value={you} top={top} label={r.you == null ? '—' : f(you)} strong />
                            <Bar value={them} top={top} label={r.them == null ? '—' : f(them)} />
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

const Bar: React.FC<{ value: number; top: number; label: string; strong?: boolean }> = ({ value, top, label, strong }) => (
    <div className="flex items-center gap-2">
        <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-muted">
            <div
                className={cn('h-full rounded-full transition-[width] duration-500', strong ? 'bg-foreground' : 'bg-foreground/30')}
                style={{ width: `${Math.max(value > 0 ? 2 : 0, (value / top) * 100)}%` }}
            />
        </div>
        <span className={cn('w-16 shrink-0 text-end text-xs tabular-nums', strong ? 'font-medium' : 'text-muted-foreground')}>{label}</span>
    </div>
);
